import React, { useState, useEffect } from 'react';
import { Navigate, useParams } from 'react-router-dom';
import { Trophy, Shield, Users } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Loading Spinner Component - Shared across route guards
 */
const LoadingSpinner = ({ message = 'Loading...' }) => (
  <div className="min-h-screen bg-gradient-to-br from-blue-900 via-blue-800 to-blue-950 flex items-center justify-center">
    <div className="text-center">
      <div className="w-16 h-16 bg-gradient-to-r from-yellow-500 to-yellow-600 rounded-full flex items-center justify-center mx-auto mb-4">
        <Trophy className="w-8 h-8 text-blue-900 animate-pulse" />
      </div>
      <p className="text-slate-50">{message}</p>
    </div>
  </div>
);

/**
 * Access Denied Component - Shown when user lacks required role
 */
const AccessDenied = ({ requiredRole, userRole, redirectPath = '/dashboard' }) => (
  <div className="min-h-screen bg-gradient-to-br from-blue-900 via-blue-800 to-blue-950 flex items-center justify-center p-4">
    <div className="text-center max-w-md">
      <div className="w-20 h-20 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
        <Shield className="w-10 h-10 text-red-400" />
      </div>
      <h1 className="text-2xl font-bold text-white mb-2">Access Denied</h1>
      <p className="text-gray-300 mb-6">
        This page is only accessible to <span className="text-yellow-400 font-semibold">{requiredRole}s</span>.
        {userRole && (
          <span className="block mt-2 text-sm text-gray-400">
            Your current role: <span className="text-blue-400">{userRole}</span>
          </span>
        )}
      </p>
      <a
        href={redirectPath}
        className="inline-block px-6 py-3 bg-yellow-500 hover:bg-yellow-600 text-blue-900 font-semibold rounded-lg transition-colors"
      >
        Go to Dashboard
      </a>
    </div>
  </div>
);

/**
 * ProtectedRoute - Base guard that requires authentication
 * Redirects to /auth if not logged in
 */
export const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return <LoadingSpinner />;
  }

  return user ? children : <Navigate to="/auth" replace />;
};

/**
 * CoachRoute - Guard that requires coach role
 * Shows access denied if user is not a coach
 */
export const CoachRoute = ({ children, redirectOnDeny = false }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return <LoadingSpinner message="Verifying coach access..." />;
  }

  if (!user) {
    return <Navigate to="/auth" replace />;
  }

  if (user.role !== 'coach') {
    if (redirectOnDeny) {
      return <Navigate to="/dashboard" replace />;
    }
    return <AccessDenied requiredRole="coach" userRole={user.role} />;
  }

  return children;
};

/**
 * AthleteRoute - Guard that requires athlete role
 * Shows access denied if user is not an athlete
 */
export const AthleteRoute = ({ children, redirectOnDeny = false }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return <LoadingSpinner message="Verifying athlete access..." />;
  }

  if (!user) {
    return <Navigate to="/auth" replace />;
  }

  if (user.role !== 'athlete') {
    if (redirectOnDeny) {
      return <Navigate to="/dashboard" replace />;
    }
    return <AccessDenied requiredRole="athlete" userRole={user.role} />;
  }

  return children;
};

/**
 * AdminRoute - Guard that requires admin role
 * For future admin panel features
 */
export const AdminRoute = ({ children, redirectOnDeny = false }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return <LoadingSpinner message="Verifying admin access..." />;
  }

  if (!user) {
    return <Navigate to="/auth" replace />;
  }

  // Admin can be a dedicated role or a coach with admin privileges
  const isAdmin = user.role === 'admin' || user.is_admin === true;
  
  if (!isAdmin) {
    if (redirectOnDeny) {
      return <Navigate to="/dashboard" replace />;
    }
    return <AccessDenied requiredRole="admin" userRole={user.role} />;
  }

  return children;
};

/**
 * RoleRoute - Generic guard that accepts any role(s)
 * Flexible for custom role requirements
 * 
 * @example
 * <RoleRoute allowedRoles={['coach', 'admin']}>
 *   <SomePage />
 * </RoleRoute>
 */
export const RoleRoute = ({ children, allowedRoles = [], redirectOnDeny = false }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return <LoadingSpinner />;
  }

  if (!user) {
    return <Navigate to="/auth" replace />;
  }

  const hasAccess = allowedRoles.includes(user.role) || 
                    (allowedRoles.includes('admin') && user.is_admin);

  if (!hasAccess) {
    if (redirectOnDeny) {
      return <Navigate to="/dashboard" replace />;
    }
    return (
      <AccessDenied 
        requiredRole={allowedRoles.join(' or ')} 
        userRole={user.role} 
      />
    );
  }

  return children;
};

/**
 * GuestRoute - Only accessible when NOT logged in
 * Useful for login/register pages that should redirect authenticated users
 */
export const GuestRoute = ({ children, redirectTo = '/dashboard' }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return <LoadingSpinner />;
  }

  return user ? <Navigate to={redirectTo} replace /> : children;
};

/**
 * TeamNotFound - Shown when team doesn't exist or user doesn't have access
 */
const TeamNotFound = ({ message = "Team not found or you don't have access" }) => (
  <div className="min-h-screen bg-gradient-to-br from-blue-900 via-blue-800 to-blue-950 flex items-center justify-center p-4">
    <div className="text-center max-w-md">
      <div className="w-20 h-20 bg-yellow-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
        <Users className="w-10 h-10 text-yellow-400" />
      </div>
      <h1 className="text-2xl font-bold text-white mb-2">Team Access Denied</h1>
      <p className="text-gray-300 mb-6">{message}</p>
      <a
        href="/dashboard"
        className="inline-block px-6 py-3 bg-yellow-500 hover:bg-yellow-600 text-blue-900 font-semibold rounded-lg transition-colors"
      >
        Go to Dashboard
      </a>
    </div>
  </div>
);

/**
 * TeamMemberRoute - Guard that checks if user belongs to a specific team
 * 
 * Validates:
 * - User is authenticated
 * - Team exists
 * - User is a member (athlete in team.players) OR owner (coach with team.coach_id)
 * 
 * Uses :teamId from route params
 * 
 * @param {boolean} coachOnly - If true, only team coach can access (not just members)
 * @param {string} teamIdParam - Custom param name for team ID (default: 'teamId')
 * 
 * @example
 * <Route path="/team/:teamId" element={
 *   <TeamMemberRoute>
 *     <TeamPage />
 *   </TeamMemberRoute>
 * } />
 */
export const TeamMemberRoute = ({ 
  children, 
  coachOnly = false,
  teamIdParam = 'teamId',
  redirectOnDeny = false 
}) => {
  const { user, loading: authLoading } = useAuth();
  const params = useParams();
  const teamId = params[teamIdParam];
  
  const [teamLoading, setTeamLoading] = useState(true);
  const [hasAccess, setHasAccess] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const checkTeamAccess = async () => {
      if (!user || !teamId) {
        setTeamLoading(false);
        return;
      }

      try {
        const response = await axios.get(`${API}/teams/${teamId}`);
        const team = response.data;
        
        // Check access based on role
        if (coachOnly) {
          // Only the team's coach can access
          setHasAccess(user.role === 'coach' && team.coach_id === user.id);
        } else {
          // Athletes: must be in players array
          // Coaches: must be the team's coach
          const isTeamCoach = user.role === 'coach' && team.coach_id === user.id;
          const isTeamMember = user.role === 'athlete' && team.players?.includes(user.id);
          setHasAccess(isTeamCoach || isTeamMember);
        }
      } catch (err) {
        // 403 or 404 means no access or team doesn't exist
        setError(err.response?.data?.detail || "Team not found or access denied");
        setHasAccess(false);
      } finally {
        setTeamLoading(false);
      }
    };

    if (!authLoading && user) {
      checkTeamAccess();
    } else if (!authLoading) {
      setTeamLoading(false);
    }
  }, [user, teamId, authLoading, coachOnly]);

  // Show loading while checking auth
  if (authLoading) {
    return <LoadingSpinner />;
  }

  // Not authenticated - redirect to auth
  if (!user) {
    return <Navigate to="/auth" replace />;
  }

  // Show loading while checking team access
  if (teamLoading) {
    return <LoadingSpinner message="Verifying team access..." />;
  }

  // No team ID in URL
  if (!teamId) {
    return <TeamNotFound message="No team specified" />;
  }

  // Access denied
  if (!hasAccess) {
    if (redirectOnDeny) {
      return <Navigate to="/dashboard" replace />;
    }
    return <TeamNotFound message={error || "You're not a member of this team"} />;
  }

  return children;
};

/**
 * TeamOwnerRoute - Alias for TeamMemberRoute with coachOnly=true
 * Only the team's coach can access these routes
 */
export const TeamOwnerRoute = ({ children, teamIdParam = 'teamId', redirectOnDeny = false }) => (
  <TeamMemberRoute coachOnly teamIdParam={teamIdParam} redirectOnDeny={redirectOnDeny}>
    {children}
  </TeamMemberRoute>
);

// Default export for backward compatibility
export default ProtectedRoute;
